import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StoreLayout } from "@/components/layout/StoreLayout";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getPaymentStatus } from "@/lib/api/orders";
import { clearCart } from "@/lib/api/cart";
import { resetCartToken } from "@/lib/api/session";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { cartQuery, myOrdersQuery } from "@/lib/api/queries";
import { queryCache } from "@/lib/api/queryCache";

export default function OrderVerify() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const orderId = params.get("sessionId") || params.get("session_id") || params.get("orderId") || params.get("order_id") || params.get("id") || "";
  const statusParam = params.get("status") || params.get("invoice_status") || "";
  const errorMessage = params.get("errorMessage") || params.get("error_message") || params.get("message") || "";

  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<string>("pending");
  const [orderNumber, setOrderNumber] = useState<string>("");

  useEffect(() => {
    if (!orderId) {
      toast.error("Invalid request: Missing Order or Session ID");
      navigate("/");
      return;
    }

    let intervalId: any;
    let attempts = 0;
    const maxAttempts = 10;

    const handleSuccessCleanup = async () => {
      try {
        await clearCart();
      } catch (err) {
        console.warn("Failed to clear cart:", err);
      }
      resetCartToken();
      queryCache.invalidate(cartQuery().queryKey);
      queryCache.invalidate(myOrdersQuery().queryKey);
      queryClient.invalidateQueries({ queryKey: cartQuery().queryKey });
      queryClient.invalidateQueries({ queryKey: myOrdersQuery().queryKey });
    };

    const checkStatus = async () => {
      try {
        const res = await getPaymentStatus(orderId, statusParam);
        setOrderNumber(res.orderNumber);
        if (res.paymentStatus === "paid") {
          await handleSuccessCleanup();
          setPaymentStatus("paid");
          setLoading(false);
          if (intervalId) clearInterval(intervalId);
          return;
        } else if (res.paymentStatus === "failed" || res.paymentStatus === "expired") {
          setPaymentStatus(res.paymentStatus);
          setLoading(false);
          if (intervalId) clearInterval(intervalId);
          return;
        }
      } catch (err) {
        console.error("Error verifying payment status:", err);
      }

      attempts++;
      if (attempts >= maxAttempts) {
        if (statusParam === "success" || statusParam === "paid") {
          await handleSuccessCleanup();
          setPaymentStatus("paid");
        } else {
          setPaymentStatus("failed");
        }
        setLoading(false);
        if (intervalId) clearInterval(intervalId);
      }
    };

    checkStatus();
    intervalId = setInterval(checkStatus, 3000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [orderId, statusParam, navigate, queryClient]);

  return (
    <StoreLayout>
      <div className="container mx-auto py-20 text-center max-w-lg px-4">
        {loading ? (
          <div className="space-y-6">
            <Loader2 className="w-16 h-16 animate-spin mx-auto text-primary" />
            <h1 className="font-headline-md text-2xl font-bold">
              {t("verify.checking", { defaultValue: "Verifying payment..." })}
            </h1>
            <p className="text-on-surface-variant text-sm">
              {t("verify.pleaseWait", {
                defaultValue: "Please wait while we confirm your transaction. Do not refresh or close this window.",
              })}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {paymentStatus === "paid" ? (
              <>
                <div className="relative inline-flex">
                  <div className="absolute inset-0 rounded-full bg-emerald-500/20 blur-2xl" />
                  <CheckCircle2 className="w-24 h-24 mx-auto text-emerald-500 relative" strokeWidth={1.5} />
                </div>
                <h1 className="font-headline-md text-4xl md:text-5xl font-bold mt-8 mb-3">
                  {t("success.title", { defaultValue: "Thank you for your order!" })}
                </h1>
                <p className="text-on-surface-variant text-lg mb-2">
                  {t("success.subtitle", { defaultValue: "Your payment was successful and your order is confirmed." })}
                </p>
                {orderNumber && (
                  <div className="inline-block bg-surface-container-low rounded-xl px-6 py-4 mb-8 border border-outline-variant/30">
                    <p className="text-xs uppercase tracking-widest text-on-surface-variant mb-1">
                      {t("success.orderNumber", { defaultValue: "Order Number" })}
                    </p>
                    <p className="font-mono text-2xl font-bold text-primary tracking-wider">
                      {orderNumber}
                    </p>
                  </div>
                )}
                <div className="flex gap-4 justify-center">
                  <Button onClick={() => navigate("/")} size="lg" className="bg-primary text-on-primary">
                    {t("success.backHome", { defaultValue: "Back to Home" })}
                  </Button>
                  <Button onClick={() => navigate("/profile")} variant="outline" size="lg">
                    {t("account.orders", { defaultValue: "My Orders" })}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="relative inline-flex">
                  <div className="absolute inset-0 rounded-full bg-destructive/20 blur-2xl" />
                  <XCircle className="w-24 h-24 mx-auto text-destructive relative" strokeWidth={1.5} />
                </div>
                <h1 className="font-headline-md text-4xl md:text-5xl font-bold mt-8 mb-3 text-destructive">
                  {t("verify.failedTitle", { defaultValue: "Payment Failed" })}
                </h1>
                <p className="text-on-surface-variant text-lg mb-8">
                  {errorMessage || t("verify.failedSubtitle", {
                    defaultValue: "We could not capture your payment. Please try placing your order again or choose Cash on Delivery.",
                  })}
                </p>
                <div className="flex gap-4 justify-center">
                  <Button onClick={() => navigate("/checkout")} size="lg" className="bg-primary text-on-primary">
                    {t("verify.tryAgain", { defaultValue: "Try Again" })}
                  </Button>
                  <Button onClick={() => navigate("/")} variant="outline" size="lg">
                    {t("success.backHome", { defaultValue: "Back to Home" })}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </StoreLayout>
  );
}
